import { normalize } from "./normalize";

/**
 * Léxico de sinônimos: como o usuário fala → termo canônico da base.
 *
 * É aqui que mora metade do ganho da recuperação. "como faço pra cobrar o
 * inquilino" só encontra a seção "Registrar o pagamento" porque alguém mapeou
 * cobrar → cobranca à mão.
 *
 * Cada entrada registra POR QUE existe. Sem isso, o léxico vira uma pilha de
 * palavras que ninguém ousa mexer, e a manutenção para.
 *
 * Fontes das palavras, nesta ordem de confiança:
 *   1. rótulos e mensagens da própria interface (medidos no código);
 *   2. vocabulário corrente de locação no Brasil;
 *   3. perguntas com 👎 em produção — o ativo mais valioso, ainda por colher.
 */
export const LEXICON: Record<string, string[]> = {
  // A tela se chama "Painel Financeiro" e a entidade, "cobrança". O usuário diz
  // fatura, boleto e conta — nenhum desses termos aparece na interface.
  cobranca: ["fatura", "boleto", "conta", "mensalidade", "parcela", "aluguel do mes", "cobrar"],

  // O botão é "Registrar Pagamento". Ninguém pergunta "como registro" — pergunta
  // "recebi, e agora?".
  pagamento: ["pagar", "recebi", "recebimento", "quitar", "baixa", "dar baixa", "compensar"],

  // O sistema calcula multa e juros sozinho ao vencer; a dúvida chega como
  // "por que aumentou".
  multa: ["juros", "acrescimo", "encargo", "aumentou", "valor a mais", "atraso", "mora"],

  // Excluir é irreversível e aparece em vários menus com nomes diferentes.
  cancelar: ["excluir", "apagar", "remover", "deletar", "anular", "estornar", "desfazer", "voltar atras"],

  // Exportação existe como "CSV", "PDF" e "Exportar" conforme a tela.
  exportar: ["baixar", "download", "planilha", "csv", "pdf", "excel", "imprimir", "salvar arquivo"],

  // Bloqueio por plano é a causa mais comum de "sumiu" e "não consigo".
  plano: ["limite", "bloqueado", "cadeado", "upgrade", "assinatura", "premium", "plus", "gratis", "nao consigo criar"],

  // Status de imóvel e de cobrança usam as mesmas palavras do dia a dia.
  status: ["situacao", "estado", "pendente", "vencido", "pago", "cancelado", "atrasado", "em aberto"],

  // "Contrato" é o que gera as cobranças; o usuário fala em "alugar".
  contrato: ["locacao", "alugar", "aluguel", "arrendamento", "renovar", "vigencia"],

  imovel: ["propriedade", "casa", "apartamento", "apto", "sala", "terreno", "unidade", "kitnet"],

  inquilino: ["locatario", "morador", "hospede", "quem aluga", "cliente"],

  // Reajuste é anual e sempre gera dúvida sobre o índice.
  reajuste: ["igpm", "ipca", "correcao", "aumento anual", "indice", "reajustar"],

  garantia: ["caucao", "fiador", "seguro fianca", "deposito", "calcao"],

  // Acesso: as palavras vêm da tela de login e do fluxo de recuperação.
  senha: ["login", "entrar", "acesso", "esqueci", "recuperar", "redefinir", "nao consigo entrar"],

  // Falhas de carregamento e de rede chegam sempre com estas palavras.
  erro: ["nao funciona", "travou", "deu problema", "bugou", "nao carrega", "em branco", "sumiu"],

  // A busca por "imposto" tem de achar carnê-leão e IRPF, que são os nomes oficiais.
  imposto: ["ir", "irpf", "leao", "carne leao", "darf", "receita federal", "declaracao", "fiscal"],

  notificacao: ["aviso", "alerta", "sino", "lembrete", "email automatico"],

  // Recibo e comprovante são a mesma coisa para quem pede.
  recibo: ["comprovante", "declaracao de quitacao", "documento de pagamento"],

  // "Vencimento" aparece em contrato, cobrança e alerta.
  vencimento: ["prazo", "data limite", "quando vence", "dia de pagamento", "dia do pagamento"],

  // A marca mudou de GFI para GIFI, e a base foi toda reescrita — mas o mundo
  // fora dela não. O endereço de acesso, o e-mail do suporte e as vídeo-aulas
  // ainda dizem GFI, então é assim que a pergunta continua chegando por um bom
  // tempo. Sem esta linha, quem escreve o nome antigo procura uma palavra que
  // não existe mais em documento nenhum.
  //
  // Só a marca entra aqui. "Sistema", "app" e "plataforma" são tentadores e
  // seriam um erro: aparecem em pergunta sobre qualquer assunto, e apontá-las
  // para a marca sujaria a cobertura ponderada por IDF, que é o corte que zera
  // as respostas confiantes fora de escopo.
  // A Vitrine entrou em 06/09 e o léxico não tinha uma palavra dela. Quem
  // procura prestador não diz "vitrine": diz "quem faz", "achar pedreiro",
  // "classificados". E quem anuncia chama o anúncio de "publicação".
  vitrine: ["classificados", "mural", "quem faz", "achar profissional", "achar prestador", "marketplace"],
  anuncio: ["anuncio", "publicacao", "divulgacao", "propaganda", "anunciar"],
  anunciante: ["prestador", "profissional", "parceiro", "fornecedor", "empresa"],
  verificado: ["selo", "confiavel", "confianca", "reputacao", "avaliado"],
  pausar: ["suspender", "tirar do ar", "congelar", "parar", "ferias"],
  denuncia: ["denunciar", "reportar", "reclamar", "golpe", "fraude", "enganoso"],
  lead: ["contato", "interessado", "mensagem recebida", "quem me procurou"],

  gifi: ["gfi"],
};

/**
 * Duas estruturas, não uma.
 *
 * `INDICE` guarda variações de UMA palavra. `FRASES` guarda as de várias, e elas
 * só valem quando todos os seus tokens estão na pergunta.
 *
 * Registrar frase token a token foi um erro medido: "nao consigo entrar" fazia
 * `consigo` apontar para `senha`, e a pergunta "não consigo apagar o imóvel"
 * respondia sobre recuperação de senha, com pontuação alta. Uma palavra comum
 * dentro de uma expressão não carrega o sentido da expressão.
 */
const INDICE = new Map<string, Set<string>>();
const FRASES: Array<{ tokens: string[]; canonico: string }> = [];

for (const [canonico, variacoes] of Object.entries(LEXICON)) {
  const canon = normalize(canonico)[0] ?? canonico;
  const registrar = (token: string) => {
    if (!INDICE.has(token)) INDICE.set(token, new Set());
    INDICE.get(token)!.add(canon);
  };

  for (const token of normalize(canonico)) registrar(token);

  for (const variacao of variacoes) {
    const tokens = normalize(variacao);
    if (tokens.length === 1) registrar(tokens[0]);
    else if (tokens.length > 1) FRASES.push({ tokens, canonico: canon });
  }
}

const FLEXOES: Record<string, string> = {
  excluo: "excluir", exporto: "exportar", cancelo: "cancelar", registro: "registrar",
  cadastro: "cadastrar", gero: "gerar", envio: "enviar", pago: "pagar",
  edito: "editar", salvo: "salvar", filtro: "filtrar", busco: "buscar",
  apago: "apagar", removo: "remover", crio: "criar", vejo: "ver",
};

/**
 * Acrescenta os termos canônicos correspondentes aos tokens da consulta.
 * Os originais são preservados: o léxico soma sentido, não substitui.
 */
export function expandWithLexicon(tokens: string[]): string[] {
  const saída = new Set(tokens);
  const presentes = new Set(tokens);

  for (const token of tokens) {
    for (const canonico of INDICE.get(token) ?? []) saída.add(canonico);
    const infinitivo = FLEXOES[token];
    if (infinitivo) {
      saída.add(infinitivo);
      for (const canonico of INDICE.get(infinitivo) ?? []) saída.add(canonico);
    }
  }
  for (const frase of FRASES) {
    if (frase.tokens.every((t) => presentes.has(t))) saída.add(frase.canonico);
  }
  return [...saída];
}

/**
 * Termos canônicos de um token, sem os originais.
 *
 * A cobertura da consulta usa isto: quando alguém escreve "boleto" e o documento
 * fala em "cobrança", a pergunta FOI entendida, e contá-la como não coberta
 * puniria justamente o caso que o léxico existe para resolver.
 */
export function canonicalsOf(token: string): string[] {
  return [...(INDICE.get(token) ?? [])];
}

/**
 * Flexões verbais na primeira pessoa, que é como as pessoas perguntam:
 * "como excluo", "onde exporto", "quando cancelo". A radicalização genérica não
 * as alcança sem cortar demais e casar palavras diferentes, então elas entram
 * como dado, uma a uma, junto do infinitivo que já existe na base.
 */

/** Devolve o infinitivo quando o token é uma flexão conhecida. */
export function inflectionOf(token: string): string | undefined {
  return FLEXOES[token];
}

/** Quantos termos o léxico conhece. Usado no relatório de cobertura. */
export const LEXICON_SIZE = INDICE.size;
